/**
 * Supplemental physician orders (Phase 8 §3.9, ORD-09).
 * Items `SK ORDER#{id}` under the patient. A signed copy is uploaded with a pre-signed POST;
 * the order is SIGNED once signedDocumentId names that copy.
 */

import type { OrderingPhysician } from './medication.dto';
import type { OrderDeadline } from '../clinical/poc-certification.dto';
import { ActiveDisciplineCode, disciplineCodes, providerTypeEntries } from '../../constants/disciplines';

export type OrderType = 'VERBAL' | 'WRITTEN';
export type OrderStatus = 'UNSIGNED' | 'SIGNED';

export const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  VERBAL: 'Verbal',
  WRITTEN: 'Written',
};

/** Phase 19 F20: disciplines an order can be for */
export type OrderDiscipline = ActiveDisciplineCode | 'LVN';
/** Phase 20A: registry disciplines, with LVN after RN (mirrors backend orders.js) */
export const ORDER_DISCIPLINES: OrderDiscipline[] = disciplineCodes().flatMap(code => [
  code,
  ...providerTypeEntries().filter(p => p.discipline === code && p.limitedNursing).map(p => p.code),
]) as OrderDiscipline[];

/** Phase 19 F20: how an order was sent to the physician for signature */
export type OrderSentMethod = 'FAX' | 'MAIL' | 'PORTAL' | 'HAND';
export const ORDER_SENT_METHOD_LABELS: Record<OrderSentMethod, string> = {
  FAX: 'Fax',
  MAIL: 'Mail',
  PORTAL: 'Portal',
  HAND: 'Hand delivery',
};

/** Staff member who took the order (server-set from the logged-in user) */
export interface OrderTakenBy {
  userId: string;
  name?: string | null;
  credentials?: string | null;
}

/** Server-computed countersignature window (null when signed or for legacy orders) */
export interface OrderCountersign {
  due: string;                // YYYY-MM-DD
  daysLeft: number;
  overdue: boolean;
}

export interface OrderDocument {
  documentId: string;
  documentName?: string;
  documentType?: string;
  uploadedAt?: string;
  uploadedBy?: string;
  /** Set when the document was removed; such entries are hidden in the UI */
  deletedAt?: string | null;
}

export interface OrderDocumentUrlResponse {
  url: string;
  expiresIn: number;
}

export interface PhysicianOrder {
  orderId: string;
  date: string;               // YYYY-MM-DD
  type: OrderType;
  summary: string;
  orderingPhysician: OrderingPhysician;
  status: OrderStatus;
  signedDocumentId?: string | null;
  documents?: OrderDocument[];
  /** Server-computed ORD-04 badge (amber > 7 days unsigned, red > 14) */
  deadline?: OrderDeadline | null;
  /** 1 = legacy (pre Phase 19), 2 = structured order */
  detailVersion?: number;
  takenBy?: OrderTakenBy | null;
  takenAt?: string | null;    // ISO date-time
  contactPerson?: string | null;
  readBack?: boolean;
  discipline?: OrderDiscipline | null;
  service?: string | null;
  frequency?: string | null;
  duration?: string | null;
  effectiveFrom?: string | null; // YYYY-MM-DD
  effectiveTo?: string | null;   // YYYY-MM-DD
  countersignDue?: string | null;
  countersign?: OrderCountersign | null;
  sentAt?: string | null;
  sentMethod?: OrderSentMethod | null;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
}

export interface CreateOrderRequest {
  date: string;
  type: OrderType;
  summary: string;
  orderingPhysician: OrderingPhysician;
  /** Phase 19 F20 structured fields; VERBAL requires takenAt, contactPerson, readBack true, service, frequency. Never send takenBy. */
  takenAt?: string;
  contactPerson?: string;
  readBack?: boolean;
  discipline?: OrderDiscipline;
  service?: string;
  frequency?: string;
  duration?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  sentAt?: string;
  sentMethod?: OrderSentMethod;
}

export type UpdateOrderRequest = Partial<CreateOrderRequest> & { signedDocumentId?: string };

/** GET …/orders/{orderId}/pdf: short-lived link to the order sheet PDF */
export interface OrderPdfUrlResponse {
  url: string;
  fileName: string;
  expiresIn: number;
}

export interface ListOrdersResponse {
  orders: PhysicianOrder[];
}

/** POST …/orders/{orderId}/documents (confirm a pre-signed upload) */
export interface OrderDocumentConfirmRequest {
  key: string;
  documentName: string;
  documentType: string;
}
